import { useInView, UseInViewOptions } from 'motion/react'
import { useEffect, useRef } from 'react'

/**
 * Invisible element that reports its "in-view" status.
 *
 * Typical usage: place a <ScrollSentinel /> at the end of a scrolling list
 * to know when the list bottom has entered the viewport—ideal for kicking
 * off "load more" requests in an infinite-scroll UI.
 *
 * Internally it wraps Framer Motion's `useInView` hook and forwards any
 * `UseInViewOptions`, giving you full control over root margin, threshold,
 * one-time observation, etc.
 *
 * Props
 * ───────────────────────────────────────────────────────────────────────────
 * @prop {function(boolean): void} [onInViewChange]
 *        Fired whenever the sentinel enters or leaves the viewport.
 *
 * @prop {UseInViewOptions}        [useInViewOptions]
 *        Options passed straight through to `useInView`.
 *
 * Render
 * ───────────────────────────────────────────────────────────────────────────
 * Produces a `<span className="block" />` that occupies no visual space
 * beyond normal document flow; its sole purpose is to act as the
 * intersection observer target.
 */
export function ScrollSentinel({
  onInViewChange,
  useInViewOptions
}: {
  onInViewChange?: (inView: boolean) => void
  useInViewOptions?: UseInViewOptions
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, useInViewOptions)

  useEffect(() => {
    if (onInViewChange) {
      onInViewChange(inView)
    }
  }, [inView, onInViewChange])

  return <span className="block" ref={ref} />
}
